import React, { useMemo, useState } from 'react';
import AdminUtils from 'services/AdminUtils';
import CalendarUtils from 'services/CalendarUtils';
import ChatUtils from 'services/ChatUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import MiscUtils from 'services/MiscUtils';
import { api } from 'careand-redux/api/ApiThunks';
import DocumentUtils from 'services/DocumentUtils';
import Icon from 'components/Utils/NewIcon';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import DocumentPreview from 'components/Utils/DocumentPreview';
import { isCareyMessage, CAREY_DISPLAY_NAME } from 'constants/carey';

const START_BLOCK = '__BLOCKQUOTE__';
const END_BLOCK = '__/BLOCKQUOTE__';

const MessageContent = ({ message, messageContent, patient, admins }) => {
  const [original, setOriginal] = useState(false);

  const { staff_id = '', attachments = [], content, to } = messageContent;
  // Carey replies carry a staff_id that doesn't resolve to a real admin, so they'd render as
  // "Unknown Name (Staff)". Detect Carey the same way the chat does (author_role attribute) and
  // show its display name instead.
  const isCarey = isCareyMessage(messageContent);
  let from = staff_id
    ? AdminUtils.getName(admins[staff_id]) + ' (Staff)'
    : messageContent.from || message.from;
  if (staff_id.toLowerCase() === 'system') from = 'Care& System';
  if (isCarey) from = CAREY_DISPLAY_NAME;
  const date = CalendarUtils.format(messageContent.date, 'Do MMM YYYY h:mm A');
  // Carey replies are outgoing (clinic side), so they sit on the same "own" side as staff replies.
  const own = !!staff_id;

  const messages = {
    simplified: ChatUtils.cleanMessageContent(content, false),
    original: ChatUtils.cleanMessageContent(content, true),
  };

  const allowShowOriginal = messages.simplified.length !== messages.original.length;

  return (
    <div className={`message ${own ? 'own' : 'yours'}`}>
      <div className="border" />
      <div className="message-block">
        <span className="date">{date}</span>
        <span className="name">From: {from}</span>
        {!!to && (
          <>
            <br />
            <span className="name">To: {(to || []).join(', ')}</span>
          </>
        )}
        <pre className="message-text">
          <BlockText text={original ? messages.original : messages.simplified} />
        </pre>
        {attachments.length > 0 && (
          <>
            <div className="message-attachments">
              {attachments.map((attachment) => (
                <Attachment
                  message={messageContent}
                  attachment={attachment}
                  patient={patient}
                  key={attachment}
                />
              ))}
            </div>
            <span>{attachments.length} attachment(s)</span>
          </>
        )}
        {allowShowOriginal && (
          <a className="bold text-primary pointer block" onClick={() => setOriginal(!original)}>
            (see {original ? 'simplified' : 'full'} message)
          </a>
        )}
      </div>
    </div>
  );
};

const MARKER_REGEX = new RegExp(`(${END_BLOCK}|${START_BLOCK})`, 'g');

const BlockText = ({ text }) => {
  function tokenize(str) {
    let nestedIndex = 0;
    let currentLines = [];
    let results = [];

    function addBlock() {
      if (currentLines.length > 0) {
        results.push({
          level: nestedIndex,
          block: nestedIndex > 0,
          lines: [...currentLines],
        });
        currentLines = [];
      }
    }

    // Markers (__BLOCKQUOTE__ / __/BLOCKQUOTE__) can be glued together or sit
    // inline with content (e.g. adjacent </blockquote></blockquote> in a reply
    // chain), so treat them as delimiters rather than whole-line tokens.
    str.split(MARKER_REGEX).forEach((part) => {
      if (part === START_BLOCK) {
        addBlock();
        nestedIndex++;
      } else if (part === END_BLOCK) {
        addBlock();
        if (nestedIndex > 0) nestedIndex--;
      } else if (part !== '') {
        part.split('\n').forEach((line) => currentLines.push(line));
      }
    });

    addBlock();

    return results.filter((block) => block.lines.join('\n').trim() !== '');
  }

  let blocks = useMemo(() => {
    return tokenize(text);
  }, [text]);

  return <QuotedMessage key={blocks.length} blocks={blocks} />;
};

const Attachment = ({ attachment = '', patient, message }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [savingDocument, setSavingDocument] = useState();

  const splitAttachment = attachment.split('/');
  const filename = splitAttachment[splitAttachment.length - 1];

  let nameSplit = attachment.split('/');
  let name = nameSplit[nameSplit.length - 1];

  async function onSaveDocument(document) {
    setSavingDocument(true);
    let blob = await MiscUtils.urlToBlob(data.url);
    let file = new File([blob], filename, {
      type: data.type,
    });
    const { title, document_type, patient_id } = document;
    let result;
    let options = {
      terrorTitleitle: 'Save Attachment',
    };

    if (patient_id) {
      result = await dispatch(
        api.Documents.addDocument({
          id: patient_id,
          file,
          title,
          documentType: document_type,
          approved: false,
        }),
        options,
      );
    } else {
      result = await dispatch(api.Documents.addUnassignedDocument({ file }));
    }
    if (result) {
      result = await dispatch(
        api.Documents.updateDocument({ patientDocument: { ...result, ...document } }),
        options,
      );
    }

    if (result) {
      setData();
      setSavingDocument(undefined);
      return result;
    }
  }

  async function onClick() {
    setLoading(true);
    let resource = await dispatch(api.Resource.getResource({ resource: { url: attachment } }), {
      errorTitle: 'Attachment Preview',
    });
    setLoading(false);
    if (resource) {
      let type = resource.type;
      let url = MiscUtils.getBlobUrl(resource);
      setData({ type, url });
    }
  }

  const defaultDocument = useMemo(() => {
    return {
      patient_id: patient?.id,
      physician_id: patient?.primary_physician,
      clinic_id: patient?.clinic_id,
      title: filename,
      document_type: DocumentUtils.DOCUMENT_TYPE.other,
      from: message.from,
      date: message.date,
      direction: 'Inbound',
    };
  }, [filename, patient, message.from, message.date]);

  const isAudio = ['audio/wav', 'audio/mpeg', 'audio/mp3'].includes(data?.type);

  return (
    <>
      {!data && (
        <div className="icon-wrapper">
          <Icon
            onClick={onClick}
            loading={loading}
            fontAwesome="paperclip"
            alt={name}
            showTooltip={true}
            width="15px"
            height="15px"
          />
        </div>
      )}
      {!!data &&
        (isAudio ? (
          <audio controls>
            <source src={data?.url} />
            Your browser does not support the media element.
          </audio>
        ) : (
          <DocumentDetailsModal
            title="Save Attachment"
            document={defaultDocument}
            onClose={() => setData()}
            hideEditDocument={true}
            allowLink={true}
            disableSend={true}
            allowDelete={false}
            allowExpand={false}
            allowEdit={false}
            loading={savingDocument}
            onSave={onSaveDocument}
            isOpen={!!data}
            previewElement={
              <div className="large-resource-preview">
                <DocumentPreview url={data?.url} type={data?.type} />
              </div>
            }
          />
        ))}
    </>
  );
};

const QuotedMessage = ({ blocks = [] }) => {
  const block = blocks[0];

  let { quotedBlocks, sameLevelBlocks } = useMemo(() => {
    if (!block) return {};

    let quotedBlocks = [];
    let i;
    for (i = 1; i < blocks.length && blocks[i].level > block.level; i++) {
      quotedBlocks.push(blocks[i]);
    }

    let sameLevelBlocks = [];
    for (let j = i; j < blocks.length; j++) sameLevelBlocks.push(blocks[j]);

    return { quotedBlocks, sameLevelBlocks };
  }, [blocks, block]);

  if (!block) return null;
  let className = block.level === 0 ? 'block' : 'block quote';

  return (
    <>
      <div className={className}>
        <span>{block.lines.join('\n').trim()}</span>
        <QuotedMessage blocks={quotedBlocks} />
      </div>
      {sameLevelBlocks.length > 0 && <QuotedMessage blocks={sameLevelBlocks} />}
    </>
  );
};

export default MessageContent;
